import subDays from 'date-fns/subDays';
import { useEffect, useMemo } from 'react';
import { useIntegrationsProvider } from 'shared/providers/IntegrationsProvider/context/IntegrationsProvider';
import {
  FailedWritebackAppointmentFragment,
  PimsWritebackStatus,
  useGetFailedWritebackAppointmentsQuery,
} from 'shared/types/graphql';
import useClinicUser from './useClinicUser';

interface FailedWritebackAppointmentOutput {
  showAiRecapWritebackNotification: boolean;
  count: number;
  failedWritebackAppointments: FailedWritebackAppointmentFragment[];
  refetchFailedWritebackAppointments: () => void;
}

export const useFailedWritebackAppointments = (): FailedWritebackAppointmentOutput => {
  const { currentClinic, currentClinicId } = useClinicUser();
  const { isExportToPimsSupported } = useIntegrationsProvider();
  const { aiRecapWritebackEnabled } = currentClinic?.clinicSetting || {};
  const twoDaysAgo = useMemo(() => subDays(new Date(), 2), []);

  const { data: appointmentsData, refetch: refetchFailedWritebackAppointments } =
    useGetFailedWritebackAppointmentsQuery({
      variables: {
        where: {
          clinicId: { equals: currentClinicId },
          aiRecapPimsWritebackLogEntry: {
            some: {
              writebackLog: {
                status: {
                  equals: PimsWritebackStatus.Failure,
                },
                // last 48 hours
                createdAt: {
                  gte: twoDaysAgo,
                },
              },
            },
          },
        },
      },
      skip: !currentClinicId || !isExportToPimsSupported || !aiRecapWritebackEnabled,
    });

  const failedWritebackAppointments = useMemo(
    () =>
      appointmentsData?.appointments?.filter(
        (appointment) =>
          appointment?.aiRecapPimsWritebackLogEntry[0]?.writebackLog.status === PimsWritebackStatus.Failure,
      ) || [],
    [appointmentsData],
  );

  const count = failedWritebackAppointments?.length;

  useEffect(() => {
    if (!currentClinicId || !isExportToPimsSupported || !aiRecapWritebackEnabled) {
      return;
    }

    const ONE_HOUR_IN_MS = 60 * 60 * 1000;
    const interval = setInterval(() => {
      refetchFailedWritebackAppointments();
    }, ONE_HOUR_IN_MS);
    return (): void => clearInterval(interval);
  }, [aiRecapWritebackEnabled, currentClinicId, isExportToPimsSupported, refetchFailedWritebackAppointments]);

  return {
    count,
    showAiRecapWritebackNotification: isExportToPimsSupported && !!aiRecapWritebackEnabled && count > 0,
    refetchFailedWritebackAppointments,
    failedWritebackAppointments,
  };
};
